import React from 'react';
import { Activity, TrendingUp, AlertTriangle, Calendar, Users, DollarSign } from 'lucide-react';

const PredictiveAnalyticsPanel = ({ forecasts }) => {
  const getTriggerTypeLabel = (type) => {
    const labels = {
      minimum_wage_breach: 'Minimum Wage Breach',
      overtime_rate_violation: 'Overtime Rate Violation',
      leave_entitlement_breach: 'Leave Entitlement Breach',
      contract_compliance_violation: 'Contract Compliance',
      nasfund_calculation_error: 'NASFUND Calculation Error',
      swt_calculation_error: 'SWT Calculation Error',
      working_hours_violation: 'Working Hours Violation',
      payment_delay_violation: 'Payment Delay Violation'
    };
    return labels?.[type] || type;
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-green-600 bg-green-100';
    if (confidence >= 0.6) return 'text-yellow-600 bg-yellow-100';
    return 'text-orange-600 bg-orange-100';
  };

  return (
    <div className="space-y-6">
      {/* Forecast Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <Activity className="h-5 w-5 text-purple-600" />
            <span className="text-xs font-medium text-purple-600">Total Forecasts</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{forecasts?.length}</div>
        </div>
        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <span className="text-xs font-medium text-orange-600">Early Warnings</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {forecasts?.filter(f => f?.earlyWarningTriggered)?.length}
          </div>
        </div>
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="h-5 w-5 text-blue-600" />
            <span className="text-xs font-medium text-blue-600">Predicted Cost</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            K{forecasts?.reduce((sum, f) => sum + (f?.predictedCostImpactKina || 0), 0)?.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Detailed Forecasts */}
      <div className="space-y-3">
        {forecasts?.slice(0, 5)?.map((forecast, index) => (
          <div key={index} className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-semibold text-gray-900">
                    {getTriggerTypeLabel(forecast?.violationTriggerType)}
                  </h4>
                  {forecast?.earlyWarningTriggered && (
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs font-medium rounded">
                      Early Warning
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-600">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {forecast?.forecastPeriod}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {forecast?.predictedAffectedEmployees} employees
                  </span>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                getConfidenceColor(forecast?.confidenceLevel)
              }`}>
                {(forecast?.confidenceLevel * 100)?.toFixed(0)}% confidence
              </span>
            </div>

            {/* Prediction Metrics */}
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div className="text-center p-2 bg-gray-50 rounded">
                <div className="text-xs text-gray-500 mb-1">Predicted Count</div>
                <div className="text-lg font-bold text-gray-900">{forecast?.predictedOccurrenceCount}</div>
              </div>
              <div className="text-center p-2 bg-gray-50 rounded">
                <div className="text-xs text-gray-500 mb-1">Cost Impact</div>
                <div className="text-lg font-bold text-red-600">K{forecast?.predictedCostImpactKina?.toLocaleString()}</div>
              </div>
              <div className="text-center p-2 bg-gray-50 rounded">
                <div className="text-xs text-gray-500 mb-1">Prevention Cost</div>
                <div className="text-lg font-bold text-green-600">K{forecast?.estimatedPreventionCostKina?.toLocaleString()}</div>
              </div>
            </div>

            {/* Pattern Indicators */}
            {forecast?.patternIndicators?.length > 0 && (
              <div className="pt-3 border-t border-gray-200">
                <div className="text-xs font-medium text-gray-700 mb-2">Pattern Indicators:</div>
                <div className="flex flex-wrap gap-2">
                  {forecast?.patternIndicators?.map((indicator, idx) => (
                    <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                      {indicator}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Prevention Recommendations */}
            {forecast?.preventionRecommendations?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="text-xs font-medium text-gray-700 mb-2">Prevention Recommendations:</div>
                <ul className="space-y-1">
                  {forecast?.preventionRecommendations?.slice(0, 2)?.map((rec, idx) => (
                    <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                      <TrendingUp className="h-3 w-3 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* No Forecasts Message */}
      {forecasts?.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No violation forecasts available</p>
          <p className="text-xs mt-1">Historical data is being analyzed for predictive modeling</p>
        </div>
      )}
    </div>
  );
};

export default PredictiveAnalyticsPanel;